import React, { Suspense, useEffect } from 'react';
import './test-env';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as SonnerToaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/components/auth/AuthProvider';
import { AuthRedirect } from '@/components/auth/AuthRedirect';
import { OAuthCallbackHandler } from '@/components/auth/OAuthCallbackHandler';
import ExternalBrowserRedirect from '@/components/auth/ExternalBrowserRedirect';
import { GoogleAnalyticsProvider } from '@/components/analytics/GoogleAnalyticsProvider';
import { PageViewTracker } from '@/components/analytics/PageViewTracker';
import { PublicPageSkeleton } from '@/components/ui/public-page-skeleton';
import { PrivatePageSkeleton } from '@/components/ui/private-page-skeleton';
import { CalendarBackgroundServices } from '@/lib/calendar/background-services';
import { useCrispVisibility } from '@/hooks/use-crisp-visibility';
import { CrispVisibilityManager } from '@/components/crisp/CrispVisibilityManager';
import { Navigation } from '@/components/Navigation';
import { BreadcrumbProvider } from '@/contexts/BreadcrumbContext';
import { SentryContextProvider } from '@/components/SentryContextProvider';
import { ScrollToTop } from '@/components/ScrollToTop';
import { DefaultSeo } from '@/components/seo/DefaultSeo';

// Critical routes - loaded immediately (homepage, auth, booking)
import Index from '@/pages/Index';
import LoginPage from '@/pages/LoginPage';
import SignUpPage from '@/pages/SignUpPage';
import ExternalBookingPage from '@/pages/ExternalBookingPage';
import BookingSuccessPage from '@/pages/BookingSuccessPage';
import BookingConfirmedPage from '@/pages/BookingConfirmedPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import UpdatePasswordPage from '@/pages/UpdatePasswordPage';

// Lazy load non-critical routes to reduce initial bundle size
const CalendarPage = React.lazy(() => import('@/pages/CalendarPage'));
const MeetingLinksPage = React.lazy(() => import('@/pages/MeetingLinksPage'));
const CreateMeetingLinkPage = React.lazy(() => import('@/pages/CreateMeetingLinkPage'));
const EditMeetingLinkPage = React.lazy(() => import('@/pages/EditMeetingLinkPage'));
const MeetingLinkRedirectPage = React.lazy(() => import('@/pages/MeetingLinkRedirectPage'));
const BookingsPage = React.lazy(() => import('@/pages/BookingsPage'));
const AnalyticsPage = React.lazy(() => import('@/pages/AnalyticsPage'));
const SettingsPage = React.lazy(() => import('@/pages/SettingsPage'));
const ContactsPage = React.lazy(() => import('@/pages/ContactsPage'));
const NotificationsPage = React.lazy(() => import('@/pages/NotificationsPage'));
const OnboardingPage = React.lazy(() => import('@/pages/OnboardingPage'));
const PricingSelectionPage = React.lazy(() => import('@/pages/PricingSelectionPage'));
const BookingManagementPage = React.lazy(() => import('@/pages/BookingManagementPage'));
const DailyEnergyPage = React.lazy(() => import('@/pages/DailyEnergyPage'));
const IntegrationsPage = React.lazy(() => import('@/pages/IntegrationsPage'));
const AppDetailPage = React.lazy(() => import('@/components/integrations/AppDetailPage'));
const BiostrapPage = React.lazy(() => import('@/pages/integrations/BiostrapPage'));
const OuraPage = React.lazy(() => import('@/pages/integrations/OuraPage'));
const WhoopPage = React.lazy(() => import('@/pages/integrations/WhoopPage'));
const UltrahumanPage = React.lazy(() => import('@/pages/integrations/UltrahumanPage'));

// Admin routes - lazy loaded (rarely accessed)
const AdminWaitlistPage = React.lazy(() => import('@/pages/AdminWaitlistPage'));
const AdminLayout = React.lazy(() => import('@/pages/admin/AdminLayout'));
const AdminHealthPage = React.lazy(() => import('@/pages/AdminHealthPage'));
const FeatureFlagsAdminPage = React.lazy(() => import('@/pages/FeatureFlagsAdminPage'));
const AdminProspectsPage = React.lazy(() => import('@/pages/AdminProspectsPage'));
const SandboxBookingPage = React.lazy(() => import('@/pages/SandboxBookingPage'));
const LiveProspectBookingPage = React.lazy(() => import('@/pages/LiveProspectBookingPage'));
const WearableDetailPage = React.lazy(() => import('@/pages/WearableDetailPage').then(m => ({ default: m.WearableDetailPage })));
const WearableOverviewPage = React.lazy(() => import('@/pages/WearableOverviewPage').then(m => ({ default: m.WearableOverviewPage })));

// Public marketing pages - lazy loaded
const AboutPage = React.lazy(() => import('@/pages/AboutPage'));
const PricingPage = React.lazy(() => import('@/pages/PricingPage'));
const BFCMPage = React.lazy(() => import('@/pages/BFCMPage'));
const FeaturesPage = React.lazy(() => import('@/pages/FeaturesPage'));
const SciencePage = React.lazy(() => import('@/pages/SciencePage'));
const ComparePage = React.lazy(() => import('@/pages/ComparePage'));
const CompareVsCalendlyPage = React.lazy(() => import('@/pages/CompareVsCalendlyPage'));
const CompareVsClockwisePage = React.lazy(() => import('@/pages/CompareVsClockwisePage'));
const CompareVsMotionPage = React.lazy(() => import('@/pages/CompareVsMotionPage'));
const CompareVsReclaimPage = React.lazy(() => import('@/pages/CompareVsReclaimPage'));
const CompareVsSkedpalPage = React.lazy(() => import('@/pages/CompareVsSkedpalPage'));
const CompareVsLifestackPage = React.lazy(() => import('@/pages/CompareVsLifestackPage'));
const ComparisonDetailPage = React.lazy(() => import('@/pages/ComparisonDetailPage'));

// Content pages - lazy loaded
const BlogIndexPage = React.lazy(() => import('@/pages/BlogIndexPage'));
const BlogPostPage = React.lazy(() => import('@/pages/BlogPostPage'));
const HacksPage = React.lazy(() => import('@/pages/HacksPage'));
const HackDetailPage = React.lazy(() => import('@/pages/HackDetailPage'));
const ContactPage = React.lazy(() => import('@/pages/ContactPage'));
const CareersPage = React.lazy(() => import('@/pages/CareersPage'));
const PrivacyPage = React.lazy(() => import('@/pages/PrivacyPage'));
const TermsPage = React.lazy(() => import('@/pages/TermsPage'));
const HelpPage = React.lazy(() => import('@/pages/HelpPage'));
const AffiliatesPage = React.lazy(() => import('@/pages/AffiliatesPage'));
const PaymentSuccessPage = React.lazy(() => import('@/pages/PaymentSuccessPage'));

// Help guide pages - lazy loaded
const ZoomIntegrationGuidePage = React.lazy(() => import('@/pages/ZoomIntegrationGuidePage'));
const AccountSetupGuide = React.lazy(() => import('@/pages/help/AccountSetupGuide'));
const CalendarIntegrationGuide = React.lazy(() => import('@/pages/help/CalendarIntegrationGuide'));
const EnergyForecastsGuide = React.lazy(() => import('@/pages/help/EnergyForecastsGuide'));
const FirstMeetingLinkGuide = React.lazy(() => import('@/pages/help/FirstMeetingLinkGuide'));
const EnergyCalculationGuide = React.lazy(() => import('@/pages/help/EnergyCalculationGuide'));
const WearableDevicesGuide = React.lazy(() => import('@/pages/help/WearableDevicesGuide'));
const ChronotypesGuide = React.lazy(() => import('@/pages/help/ChronotypesGuide'));
const SleepScheduleGuide = React.lazy(() => import('@/pages/help/SleepScheduleGuide'));
const EnergyAwareBookingGuide = React.lazy(() => import('@/pages/help/EnergyAwareBookingGuide'));
const MeetingPreferencesGuide = React.lazy(() => import('@/pages/help/MeetingPreferencesGuide'));
const TimeZoneHandlingGuide = React.lazy(() => import('@/pages/help/TimeZoneHandlingGuide'));
const AutomatedSchedulingRulesGuide = React.lazy(() => import('@/pages/help/AutomatedSchedulingRulesGuide'));
const CalendarSyncTroubleshootingGuide = React.lazy(() => import('@/pages/help/CalendarSyncTroubleshootingGuide'));
const WearableDeviceConnectionsGuide = React.lazy(() => import('@/pages/help/WearableDeviceConnectionsGuide'));
const ThirdPartyAppSetupGuide = React.lazy(() => import('@/pages/help/ThirdPartyAppSetupGuide'));
const ApiKeysGuide = React.lazy(() => import('@/pages/help/ApiKeysGuide'));

// NotFound - small, keep in main bundle
import NotFound from '@/pages/NotFound';

// Redirect component for sandbox -> claim migration
function SandboxRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/claim/${slug}`} replace />;
}

function SandboxBookRedirect() {
  const { slug, templateId } = useParams<{ slug: string; templateId: string }>();
  return <Navigate to={`/claim/${slug}/book/${templateId}`} replace />;
}

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

function App() {
  // Initialize calendar health monitoring and proactive refresh
  useEffect(() => {
    const backgroundServices = CalendarBackgroundServices.getInstance();
    backgroundServices.start();
    
    return () => {
      backgroundServices.stop();
    };
  }, []);

  
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <SentryContextProvider>
          <Router>
            <ScrollToTop />
            <DefaultSeo />
            <AuthProvider>
              {/* Temporarily disabled to test OAuth flow */}
              {/* <ExternalBrowserRedirect /> */}
              <OAuthCallbackHandler />
              <GoogleAnalyticsProvider measurementId="G-XXXXXXXXXX">
                  <PageViewTracker />
                  <CrispVisibilityManager />
                  <BreadcrumbProvider>
                <Routes>
                  {/* External booking routes - these should NOT be wrapped in Navigation */}
                  <Route path="/book/:businessSlug/:slug" element={<ExternalBookingPage />} />
                  <Route path="/book/:slug" element={<ExternalBookingPage />} />
                  
                  {/* Claim routes (formerly sandbox) */}
                  <Route path="/claim/:slug" element={<SandboxBookingPage />} />
                  <Route path="/claim/:slug/book/:templateId" element={<SandboxBookingPage />} />
                  
                  {/* Redirect old sandbox routes to claim */}
                  <Route path="/sandbox/:slug" element={<SandboxRedirect />} />
                  <Route path="/sandbox/:slug/book/:templateId" element={<SandboxBookRedirect />} />
                  
                  <Route path="/p/:slug" element={<LiveProspectBookingPage />} />
                  <Route path="/booking/success" element={<BookingSuccessPage />} />
                  <Route path="/booking/confirmed" element={<BookingConfirmedPage />} />
                  <Route path="/booking/:bookingId/manage" element={<ExternalBookingPage />} />
                  <Route path="/payment/success" element={<PaymentSuccessPage />} />

                  {/* Authentication routes - these should NOT be wrapped in Navigation */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignUpPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/update-password" element={<UpdatePasswordPage />} />

                  {/* All other routes wrapped in Navigation */}
                  <Route path="*" element={
                    <Navigation>
                      <Suspense fallback={<PrivatePageSkeleton />}>
                        <Routes>
                          {/* Public routes */}
                          <Route path="/" element={
                            <Suspense fallback={<PublicPageSkeleton variant="landing" />}>
                              <Index />
                            </Suspense>
                          } />
                          <Route path="/about" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <AboutPage />
                            </Suspense>
                          } />
                          <Route path="/pricing" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <PricingPage />
                            </Suspense>
                          } />
                          <Route path="/bfcm" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <BFCMPage />
                            </Suspense>
                          } />
                          <Route path="/black-friday" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <BFCMPage />
                            </Suspense>
                          } />
                          <Route path="/features" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <FeaturesPage />
                            </Suspense>
                          } />
                          <Route path="/science" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <SciencePage />
                            </Suspense>
                          } />
                          <Route path="/compare" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <ComparePage />
                            </Suspense>
                          } />
                          {/* Comparison pages - primary routes */}
                          <Route path="/compare/chronocal-vs-calendly" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <CompareVsCalendlyPage />
                            </Suspense>
                          } />
                          <Route path="/compare/chronocal-vs-clockwise" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <CompareVsClockwisePage />
                            </Suspense>
                          } />
                          <Route path="/compare/chronocal-vs-motion" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <CompareVsMotionPage />
                            </Suspense>
                          } />
                          <Route path="/compare/chronocal-vs-reclaim" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <CompareVsReclaimPage />
                            </Suspense>
                          } />
                          <Route path="/compare/chronocal-vs-skedpal" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <CompareVsSkedpalPage />
                            </Suspense>
                          } />
                          <Route path="/compare/chronocal-vs-lifestack" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <CompareVsLifestackPage />
                            </Suspense>
                          } />
                          
                          {/* Comparison pages - backward compatibility redirects */}
                          <Route path="/compare/calendly" element={<Navigate to="/compare/chronocal-vs-calendly" replace />} />
                          <Route path="/compare/clockwise" element={<Navigate to="/compare/chronocal-vs-clockwise" replace />} />
                          <Route path="/compare/motion" element={<Navigate to="/compare/chronocal-vs-motion" replace />} />
                          <Route path="/compare/reclaim" element={<Navigate to="/compare/chronocal-vs-reclaim" replace />} />
                          <Route path="/compare/skedpal" element={<Navigate to="/compare/chronocal-vs-skedpal" replace />} />
                          <Route path="/compare/lifestack" element={<Navigate to="/compare/chronocal-vs-lifestack" replace />} />
                          <Route path="/comparison/:slug" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <ComparisonDetailPage />
                            </Suspense>
                          } />
                          <Route path="/blog" element={
                            <Suspense fallback={<PublicPageSkeleton variant="blog" />}>
                              <BlogIndexPage />
                            </Suspense>
                          } />
                          <Route path="/blog/:slug" element={
                            <Suspense fallback={<PublicPageSkeleton variant="blog" />}>
                              <BlogPostPage />
                            </Suspense>
                          } />
                          <Route path="/hacks" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <HacksPage />
                            </Suspense>
                          } />
                          <Route path="/hacks/:slug" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <HackDetailPage />
                            </Suspense>
                          } />
                          <Route path="/contact" element={
                            <Suspense fallback={<PublicPageSkeleton variant="contact" />}>
                              <ContactPage />
                            </Suspense>
                          } />
                          <Route path="/careers" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <CareersPage />
                            </Suspense>
                          } />
                          <Route path="/privacy" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <PrivacyPage />
                            </Suspense>
                          } />
                          <Route path="/terms" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <TermsPage />
                            </Suspense>
                          } />
                          <Route path="/help" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <HelpPage />
                            </Suspense>
                          } />
                          <Route path="/help/integrations/zoom" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <ZoomIntegrationGuidePage />
                            </Suspense>
                          } />
                          <Route path="/help/getting-started/account-setup" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <AccountSetupGuide />
                            </Suspense>
                          } />
                          <Route path="/help/getting-started/calendar-integration" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <CalendarIntegrationGuide />
                            </Suspense>
                          } />
                          <Route path="/help/getting-started/energy-forecasts" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <EnergyForecastsGuide />
                            </Suspense>
                          } />
                          <Route path="/help/getting-started/first-meeting-link" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <FirstMeetingLinkGuide />
                            </Suspense>
                          } />
                          <Route path="/help/energy-management/energy-calculation" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <EnergyCalculationGuide />
                            </Suspense>
                          } />
                          <Route path="/help/energy-management/wearable-devices" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <WearableDevicesGuide />
                            </Suspense>
                          } />
                          <Route path="/help/energy-management/chronotypes" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <ChronotypesGuide />
                            </Suspense>
                          } />
                          <Route path="/help/energy-management/sleep-schedule" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <SleepScheduleGuide />
                            </Suspense>
                          } />
                          <Route path="/help/scheduling/energy-aware-booking" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <EnergyAwareBookingGuide />
                            </Suspense>
                          } />
                          <Route path="/help/scheduling/managing-preferences" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <MeetingPreferencesGuide />
                            </Suspense>
                          } />
                          <Route path="/help/scheduling/time-zone-handling" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <TimeZoneHandlingGuide />
                            </Suspense>
                          } />
                          <Route path="/help/scheduling/automated-rules" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <AutomatedSchedulingRulesGuide />
                            </Suspense>
                          } />
                          <Route path="/help/integrations/calendar-sync" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <CalendarSyncTroubleshootingGuide />
                            </Suspense>
                          } />
                          <Route path="/help/integrations/wearable-devices" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <WearableDeviceConnectionsGuide />
                            </Suspense>
                          } />
                          <Route path="/help/integrations/third-party-apps" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <ThirdPartyAppSetupGuide />
                            </Suspense>
                          } />
                          <Route path="/help/api-keys" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <ApiKeysGuide />
                            </Suspense>
                          } />

                          {/* Integration Marketing Pages */}
                          <Route path="/integrations/biostrap" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <BiostrapPage />
                            </Suspense>
                          } />
                          <Route path="/integrations/oura" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <OuraPage />
                            </Suspense>
                          } />
                          <Route path="/integrations/whoop" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <WhoopPage />
                            </Suspense>
                          } />
                          <Route path="/integrations/ultrahuman" element={
                            <Suspense fallback={<PublicPageSkeleton />}>
                              <UltrahumanPage />
                            </Suspense>
                          } />

                          {/* Private routes - gated by AuthRedirect */}
                          <Route element={<AuthRedirect />}>
                            <Route path="/calendar" element={
                              <Suspense fallback={<PrivatePageSkeleton variant="calendar" />}>
                                <CalendarPage />
                              </Suspense>
                            } />
                            <Route path="/meeting" element={
                              <Suspense fallback={<PrivatePageSkeleton variant="meeting-links" />}>
                                <MeetingLinksPage />
                              </Suspense>
                            } />
                            <Route path="/meeting/create" element={
                              <Suspense fallback={<PrivatePageSkeleton variant="form" />}>
                                <CreateMeetingLinkPage />
                              </Suspense>
                            } />
                            <Route path="/meeting/:id/edit" element={
                              <Suspense fallback={<PrivatePageSkeleton variant="form" />}>
                                <EditMeetingLinkPage />
                              </Suspense>
                            } />
                            <Route path="/meeting/:id" element={<MeetingLinkRedirectPage />} />
                            <Route path="/bookings" element={
                              <Suspense fallback={<PrivatePageSkeleton variant="table" />}>
                                <BookingsPage />
                              </Suspense>
                            } />
                            <Route path="/bookings/:bookingId" element={
                              <Suspense fallback={<PrivatePageSkeleton variant="table" />}>
                                <BookingsPage />
                              </Suspense>
                            } />
                            
                            <Route path="/analytics" element={
                              <Suspense fallback={<PrivatePageSkeleton variant="analytics" />}>
                                <AnalyticsPage />
                              </Suspense>
                            } />
                            <Route path="/settings" element={<SettingsPage />} />

                            <Route path="/settings/wearables" element={<WearableOverviewPage />} />
                            <Route path="/settings/wearable/:connectionId" element={<WearableDetailPage />} />
                            <Route path="/settings/calendar" element={<SettingsPage />} />
                            <Route path="/settings/availability" element={<SettingsPage />} />
                            <Route path="/settings/integrations" element={<IntegrationsPage />} />
                            <Route path="/settings/integrations/:appId" element={<AppDetailPage />} />
                            <Route path="/settings/plans" element={<SettingsPage />} />
                            <Route path="/settings/account" element={<Navigate to="/settings/plans" replace />} />
                            <Route path="/settings/notifications" element={<SettingsPage />} />
                            <Route path="/settings/api-keys" element={<SettingsPage />} />
                            <Route path="/settings/development" element={<SettingsPage />} />
                            <Route path="/contacts" element={<ContactsPage />} />
                            <Route path="/notifications" element={<NotificationsPage />} />
                            <Route path="/onboarding" element={<OnboardingPage />} />
                            <Route path="/pricing-selection" element={<PricingSelectionPage />} />
                            <Route path="/energy" element={<DailyEnergyPage />} />
                            
                            <Route path="/affiliates" element={<AffiliatesPage />} />
                            <Route path="/admin" element={<AdminLayout />} >
                              <Route index element={<Navigate to="health" replace />} />
                              <Route path="waitlist" element={<AdminWaitlistPage />} />
                              <Route path="health" element={<AdminHealthPage />} />
                              <Route path="feature-flags" element={<FeatureFlagsAdminPage />} />
                              <Route path="prospects" element={<AdminProspectsPage />} />
                            </Route>
                          </Route>

                          {/* Catch all route */}
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                    </Navigation>
                  } />
                </Routes>
              </BreadcrumbProvider>
              <Toaster />
              <SonnerToaster />
            </GoogleAnalyticsProvider>
          </AuthProvider>
        </Router>
        </SentryContextProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
